/**
 * As perguntas que o avaliador responde no local, por categoria de obra.
 *
 * Viviam só no código: acrescentar uma pergunta obrigava a publicar uma
 * versão, e as categorias já semeadas não se podiam corrigir de todo — o
 * semeador só cria o que ainda não existe. Quem sabe que perguntas fazer numa
 * remodelação é quem anda em obra, não quem escreve o código.
 *
 * O que se edita aqui só vale para levantamentos futuros. Os já entregues
 * guardam as respostas que foram dadas, com a pergunta do dia em que o foram —
 * mudar a checklist não reescreve o que um técnico mediu.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ClipboardList, Plus, Trash2, Save } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';

// Sem o âmbito à vista, duas linhas "Canalização" ficavam indistinguíveis e
// editava-se a errada.
const AMBITO = { NOVA: 'instalação nova', EXISTENTE: 'intervenção no existente' };

const ROTULO_TIPO = {
  numero: 'Número',
  texto: 'Texto livre',
  sim_nao: 'Sim / Não',
  opcoes: 'Escolha uma',
  multi: 'Escolha várias',
};

const ChecklistsPage = () => {
  const { success, error } = useToast();
  const [dados, setDados] = useState(null);
  const [aberta, setAberta] = useState(null);
  const [campos, setCampos] = useState([]);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(() => {
    api.get('/operations/checklists')
      .then((r) => setDados(r.data))
      .catch(() => { setDados({ modelos: [] }); error('Erro ao carregar as listas de verificação'); });
  }, [error]);
  useEffect(() => { carregar(); }, [carregar]);

  const abrir = (m) => {
    setAberta(m);
    setCampos((m.campos || []).map((c) => ({
      ...c, opcoesTexto: Array.isArray(c.opcoes) ? c.opcoes.join(', ') : '',
    })));
  };

  const mexer = (i, mudanca) => setCampos((v) => v.map((c, j) => (j === i ? { ...c, ...mudanca } : c)));

  const gravar = async () => {
    setBusy(true);
    try {
      await api.put(`/operations/listas de verificação/${aberta.modelo_id}`, {
        campos: campos.map((c) => ({
          chave: c.chave, rotulo: c.rotulo, tipo: c.tipo, obrigatorio: !!c.obrigatorio,
          opcoes: ['opcoes', 'multi'].includes(c.tipo)
            ? String(c.opcoesTexto || '').split(',').map((x) => x.trim()).filter(Boolean)
            : undefined,
        })),
      });
      success(`Lista de verificação de ${aberta.categoria} gravada. Vale para as próximas vistorias.`);
      setAberta(null); carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível gravar.'); }
    finally { setBusy(false); }
  };

  if (!dados) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-cyan-400" /> Listas de verificação da vistoria
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          O que o avaliador técnico mede no local, por categoria. É esta informação que
          passa para o caderno e sobre a qual as empresas fazem o preço — uma pergunta
          que falta aqui é um orçamento feito às escuras. O que alterar vale para os
          próximos levantamentos; os já entregues ficam como foram.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {dados.modelos.map((m) => (
          <button key={m.modelo_id} onClick={() => abrir(m)}
            className="text-left p-4 rounded-xl border border-slate-800 bg-slate-900 hover:border-slate-700">
            <div className="font-semibold text-white">
              {m.categoria === 'GERAL' ? 'Categorias sem lista de verificação própria' : m.categoria}
              {m.ambito && (
                <span className="ml-2 text-xs font-normal text-cyan-400">{AMBITO[m.ambito]}</span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {(m.campos || []).length} {(m.campos || []).length === 1 ? 'pergunta' : 'perguntas'}
            </div>
          </button>
        ))}
      </div>

      {aberta && (
        <div className="fixed inset-0 z-50 bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label={`Lista de verificação de ${aberta.categoria}`}>
          <div className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-700 bg-slate-900 p-5 my-6">
            <div className="flex items-start gap-3 flex-wrap">
              <h2 className="flex-1 text-lg font-bold text-white">
                {aberta.categoria === 'GERAL' ? 'Categorias sem lista de verificação própria' : aberta.categoria}
                {aberta.ambito && (
                  <span className="ml-2 text-sm font-normal text-cyan-400">{AMBITO[aberta.ambito]}</span>
                )}
              </h2>
              <button onClick={() => setAberta(null)}
                className="px-3 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Fechar</button>
            </div>

            <div className="mt-4 space-y-3">
              {campos.map((c, i) => (
                <div key={i} className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    <input value={c.rotulo} onChange={(e) => mexer(i, { rotulo: e.target.value })}
                      placeholder="Pergunta que o técnico vê"
                      className="flex-1 min-w-[180px] px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                    <select value={c.tipo} onChange={(e) => mexer(i, { tipo: e.target.value })}
                      className="px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200">
                      {(dados.tipos || Object.keys(ROTULO_TIPO)).map((t) => (
                        <option key={t} value={t}>{ROTULO_TIPO[t] || t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-2 flex-wrap items-center">
                    <input value={c.chave} onChange={(e) => mexer(i, { chave: e.target.value })}
                      placeholder="chave_interna"
                      className="w-44 px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-400 font-mono text-sm" />
                    <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={!!c.obrigatorio}
                        onChange={(e) => mexer(i, { obrigatorio: e.target.checked })} />
                      Obrigatório
                    </label>
                    <button onClick={() => setCampos((v) => v.filter((_, j) => j !== i))}
                      aria-label={`Retirar ${c.rotulo || 'campo'}`}
                      className="ml-auto px-3 min-h-[44px] rounded-lg bg-slate-800 text-red-400 border border-slate-700 inline-flex items-center gap-1">
                      <Trash2 className="w-4 h-4" /> Retirar
                    </button>
                  </div>

                  {['opcoes', 'multi'].includes(c.tipo) && (
                    <input value={c.opcoesTexto || ''} onChange={(e) => mexer(i, { opcoesTexto: e.target.value })}
                      placeholder="Opções separadas por vírgula — ex.: Boa, Parcial, Substituir toda"
                      className="w-full px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                  )}
                </div>
              ))}

              <button type="button"
                onClick={() => setCampos((v) => [...v, { chave: '', rotulo: '', tipo: 'numero', obrigatorio: false, opcoesTexto: '' }])}
                className="w-full min-h-[44px] rounded-lg border border-dashed border-slate-700 text-slate-400 text-sm inline-flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Acrescentar pergunta
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-3">
              A chave interna é o que liga a resposta ao relatório. Mudá-la numa pergunta
              antiga faz as respostas já dadas deixarem de corresponder a esta pergunta —
              não se perdem, mas passam a viver por conta própria no histórico.
            </p>

            <button onClick={gravar} disabled={busy || !campos.length}
              className="w-full mt-4 min-h-[46px] rounded-lg bg-blue-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Save className="w-4 h-4" /> Gravar lista de verificação
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistsPage;
